import m from 'mithril';
import BaseComponent from '../../BaseComponent';
import FilterSelectDropdownView from './view/FilterSelectDropdownView';
import normalizeString from "../../../helpers/normalizeString";

export interface FilterOption {
    value: string;
    label: string;
    selected: boolean;
}

export interface FilterGroup {
    label: string;
    options: FilterOption[];
}

export type FilterDisplayMode = 'text' | 'stars';

export default class FilterSelectDropdown extends BaseComponent {

    public static readonly SEARCH_THRESHOLD = 6;

    public options: FilterOption[] = [];
    public multiple = false;
    public searchQuery = '';
    public highlightedIndex = -1;
    public displayMode: FilterDisplayMode = 'text';

    private groups: FilterGroup[] | null = null;
    private $hiddenSelect: JQuery<HTMLSelectElement> | null = null;
    private view: FilterSelectDropdownView | null = null;

    public init(): void {
        const inputId = this.$component.data('filter-input-id');
        this.multiple = String(this.$component.data('filter-multiple')) === '1';
        this.displayMode = this.$component.data('filter-display') === 'stars' ? 'stars' : 'text';

        const hiddenSelect = document.getElementById(inputId) as HTMLSelectElement | null;
        if (!hiddenSelect) {
            console.warn(`FilterSelectDropdown: hidden select "${inputId}" not found`);
            return;
        }
        this.$hiddenSelect = $(hiddenSelect);

        this.parseFromSelect(hiddenSelect);

        this.view = new FilterSelectDropdownView(this);
        m.mount(this.$component[0], this.view);

        this.$component.parent().on('shown.bs.dropdown', () => {
            this.highlightedIndex = -1;
            m.redraw();
            this.focusSearch();
        });
    }

    public beforeRemove(): void {
        if (this.$component[0]) {
            m.mount(this.$component[0], null);
        }
    }

    public toggle(option: FilterOption): void {
        if (this.multiple) {
            option.selected = !option.selected;
        } else {
            if (option.selected) {
                return;
            }
            this.options.forEach(o => { o.selected = false; });
            option.selected = true;
        }

        this.syncHiddenSelect();
    }

    public setSearchQuery(value: string): void {
        if (this.searchQuery !== value) {
            this.highlightedIndex = -1;
        }
        this.searchQuery = value;
    }

    public getNavigableOptions(): FilterOption[] {
        const groups = this.getFilteredGroups();
        if (groups !== null) {
            return groups.flatMap(g => g.options);
        }
        return this.getFilteredOptions();
    }

    public moveHighlight(delta: number): void {
        const options = this.getNavigableOptions();
        if (options.length === 0) {
            this.highlightedIndex = -1;
            return;
        }
        if (this.highlightedIndex === -1) {
            this.highlightedIndex = delta > 0 ? 0 : options.length - 1;
            return;
        }
        this.highlightedIndex = (this.highlightedIndex + delta + options.length) % options.length;
    }

    public showSearch(): boolean {
        return this.options.length >= FilterSelectDropdown.SEARCH_THRESHOLD;
    }

    public getFilteredOptions(): FilterOption[] {
        return this.filterOptions(this.options);
    }

    public getFilteredGroups(): FilterGroup[] | null {
        if (this.groups === null) {
            return null;
        }
        const q = normalizeString(this.searchQuery);
        if (q === '') {
            return this.groups.filter(g => g.options.length > 0);
        }
        return this.groups
            .map(g => normalizeString(g.label).includes(q)
                ? { label: g.label, options: g.options }
                : { label: g.label, options: g.options.filter(o => normalizeString(o.label).includes(q)) })
            .filter(g => g.options.length > 0);
    }

    private filterOptions(options: FilterOption[]): FilterOption[] {
        const q = normalizeString(this.searchQuery);
        if (q === '') {
            return options;
        }
        return options.filter(o => normalizeString(o.label).includes(q));
    }

    private focusSearch(): void {
        this.$component.find('.filter-select-dropdown-search input').trigger('focus');
    }

    /**
     * Reads options from the hidden <select>. Mixed children (<option> + <optgroup>)
     * are supported: when at least one <optgroup> is present, options are grouped;
     * any loose <option> is collected into a leading unlabeled group.
     */
    private parseFromSelect(select: HTMLSelectElement): void {
        const flat: FilterOption[] = [];
        const groups: FilterGroup[] = [];
        let looseGroup: FilterGroup | null = null;

        for (const child of Array.from(select.children)) {
            if (child instanceof HTMLOptGroupElement) {
                const group: FilterGroup = { label: child.label, options: [] };
                for (const optionEl of Array.from(child.children)) {
                    if (!(optionEl instanceof HTMLOptionElement) || optionEl.value === '') {
                        continue;
                    }
                    const option = this.toOption(optionEl);
                    group.options.push(option);
                    flat.push(option);
                }
                if (group.options.length > 0) {
                    groups.push(group);
                }
            } else if (child instanceof HTMLOptionElement) {
                if (child.value === '') {
                    continue;
                }
                const option = this.toOption(child);
                flat.push(option);
                if (groups.length === 0) {
                    if (looseGroup === null) {
                        looseGroup = { label: '', options: [] };
                    }
                    looseGroup.options.push(option);
                }
            }
        }

        this.options = flat;
        this.groups = groups.length > 0
            ? (looseGroup !== null ? [looseGroup, ...groups] : groups)
            : null;
    }

    private toOption(optionEl: HTMLOptionElement): FilterOption {
        return {
            value: optionEl.value,
            label: optionEl.textContent ?? optionEl.value,
            selected: optionEl.selected,
        };
    }

    private syncHiddenSelect(): void {
        if (!this.$hiddenSelect) {
            return;
        }

        const select = this.$hiddenSelect[0];
        const selectedValues = new Set(this.options.filter(o => o.selected).map(o => o.value));

        for (const optionEl of Array.from(select.options)) {
            optionEl.selected = selectedValues.has(optionEl.value);
        }

        this.$hiddenSelect.trigger('change');
    }
}
